import { Caption1, makeStyles, tokens } from "@fluentui/react-components";
import { useState, type ReactNode } from "react";
import { SectionTitle } from "@/components/ui-typography";
import { ForecastBoard, RhythmBoard, ShareBoard, UpcomingList, type ForecastDetail, type ForecastRow, type RhythmHeatRow, type RhythmLane } from "@/components/visual/ink-board";
import { FitBoard, MatrixBoard, type FitPeak, type FitRow, type MatrixDay, type WindowRow } from "@/components/visual/ink-matrix";
import { AgendaRow, JudgmentRow, TrackRow, WeekCells } from "@/components/visual/ink";
import { ReadinessChart, ReadinessList, type ReadinessColumn } from "@/components/visual/ink-readiness";
import { IssueBoards, ProgressDelta, StatusWord, VarianceTable, type InkVarianceRow } from "@/components/visual/ink-report";
import { WBS_SNAPSHOT_WEEK_10 } from "@/features/weekly-report/weekly-report-v2-data";
import {
  RAG_LABEL,
  issueAging,
  issueTrend,
  issuesForWeek,
  latestWeek,
  milestoneSlipDays,
  personById,
  ragTone,
  slipText,
  weekById,
  weeksThrough,
} from "@/features/weekly-report/weekly-report-model";
import { WEEKLY_REPORT } from "@/features/weekly-report/weekly-report-mock";
import { addDays, toDay, weekdayOfDay } from "@/engine/date";
import { formatDate, weekdayNames } from "@/lib/date-format";

const useStyles = makeStyles({
  stack: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalXXL,
  },
  lead: {
    margin: 0,
    color: tokens.colorNeutralForeground2,
    fontWeight: tokens.fontWeightRegular,
  },
  section: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalS,
  },
  sectionHead: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalXXS,
  },
  hint: {
    color: tokens.colorNeutralForeground3,
    fontWeight: tokens.fontWeightRegular,
  },
  list: {
    display: "flex",
    flexDirection: "column",
    minWidth: 0,
  },
  words: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: "12px",
    minHeight: "32px",
  },
});

const WEEKS = [
  { key: "2026-09-21", start: "2026-09-21" },
  { key: "2026-09-28", start: "2026-09-28" },
  { key: "2026-10-05", start: "2026-10-05" },
  { key: "2026-10-12", start: "2026-10-12", delayed: true },
  { key: "2026-10-19", start: "2026-10-19", current: true },
];

function Section({ title, hint, children }: { title: string; hint: string; children: ReactNode }) {
  const styles = useStyles();
  return (
    <section className={styles.section}>
      <div className={styles.sectionHead}>
        <SectionTitle>{title}</SectionTitle>
        <Caption1 className={styles.hint}>{hint}</Caption1>
      </div>
      {children}
    </section>
  );
}

export function InkTab() {
  const styles = useStyles();
  return (
    <div className={styles.stack}>
      <p className={styles.lead}>글이 본문입니다. 색은 주의와 위험에만 쓰고, 숫자는 칸에 맞춥니다.</p>
      <ReadinessDemo />
      <WeeklyInkDemo />
      <Section title="판단 행" hint="문장 옆의 짧은 정도입니다. 공정 숫자는 막대 위에 다시 쓰지 않습니다.">
        <div className={styles.list}>
          <JudgmentRow text="구매 발주·입고 화면 연동" verdict="thisWeek" extent={70} />
          <JudgmentRow text="출하 프로세스 화면 개발" verdict="nextWeek" extent={15} />
          <JudgmentRow text="결산 연계 범위 확정" verdict="blocked" extent={20} />
        </div>
      </Section>
      <Section title="주간 칸" hint="지금 움직이는 단계의 지난 주입니다. 이번 주는 테두리, 지연된 칸만 색입니다.">
        <WeekCells
          phase="구축"
          weeks={WEEKS.map((week) => ({
            key: week.key,
            label: formatDate(week.start, "short"),
            current: week.current,
            delayed: week.delayed,
          }))}
        />
      </Section>
      <Section title="추적 행" hint="연 날부터 지금까지입니다. 해결은 표시와 코멘트, 첨부는 있는 줄만 개수입니다.">
        <div className={styles.list}>
          <TrackRow title="레거시 발주 코드 매핑" opened={formatDate("2026-10-09", "numeric")} state="late" extent={100} />
          <TrackRow
            title="결산 테스트 계정"
            opened={formatDate("2026-10-20", "numeric")}
            state="done"
            extent={100}
            comment="고객사에서 계정을 발급해 테스트에 반영했습니다."
          />
          <TrackRow title="현장 창고 실사 일정" opened={formatDate("2026-10-20", "numeric")} state="open" extent={35} attachments={["창고 실사 요청.pdf"]} />
        </div>
      </Section>
      <Section title="일정 행" hint="날짜, 이름, 지난 주 대비입니다.">
        <div className={styles.list}>
          <AgendaRow date={formatDate("2026-10-28", "numeric")} title="주간 공정 회의" shift="held" />
          <AgendaRow date={formatDate("2026-10-30", "numeric")} title="사용자 교육" shift="sooner" />
          <AgendaRow date={formatDate("2026-11-06", "numeric")} title="통합 테스트 착수 보고" shift="later" />
          <AgendaRow date={formatDate("2026-10-16", "numeric")} title="기준정보 확정" shift="done" />
        </div>
      </Section>
      <Section title="구성" hint="합계와 비중입니다. 막대는 회색이고, 숫자는 표에만 있습니다.">
        <ShareBoard
          totalLabel="이번 달"
          countLabel="5개 단계"
          parts={SHARE_PARTS}
          rows={SHARE_ROWS}
          note="합계에 넣지 않은 단계는 빠집니다."
        />
      </Section>
      <Section title="다가오는 일정" hint="날짜, 이름, 남은 날입니다.">
        <UpcomingList meta="30일 안에 4건" items={UPCOMING} />
      </Section>
      <Section title="리듬" hint="칸을 드래그하면 범위가 바뀝니다. 지연된 칸만 색입니다.">
        <RhythmBoard
          rangeLabel={`${formatDate("2026-10-09", "monthDay")} ~ ${formatDate("2026-10-22", "monthDay")}`}
          hint="6개 단계 · 칸을 드래그하면 범위가 바뀝니다"
          columns={RHYTHM_COLUMNS}
          todayIndex={6}
          lanes={RHYTHM_LANES}
          busiest="가장 바쁜 수요일 10:00"
          busiestNote="범위 안 지연 4"
          heatColumns={["00", "06", "12", "18"]}
          heatRows={HEAT_ROWS}
          dayLabels={["00:00", "06:00", "12:00", "18:00", "23:00"]}
          weekday={[0, 0, 1, 4, 8, 8, 7, 2, 1, 3]}
          weekend={[0, 0, 0, 1, 2, 2, 1, 0, 0, 1]}
        />
      </Section>
      <Section title="지금" hint="흐름은 날마다의 칸이고, 표는 사용과 속도입니다. 줄을 고르면 아래 차트가 바뀝니다.">
        <ForecastBoard headline="1개 단계가 리셋 전에 막힙니다" sub="구축이 먼저, 토요일 06:31, 이후 4일 12시간 잠김" days={FORECAST_DAYS} rows={FORECAST_ROWS} />
      </Section>
      <Section title="행렬" hint="시간대별 밀도입니다. 점은 막힌 칸이고, 아래 막대는 구간이 얼마나 찼는지입니다.">
        <MatrixBoard title="평일 09–15시가 가장 바쁩니다" note="30일 · 점 3 · 잠긴 날 4" hours={MATRIX_HOURS} days={MATRIX_DAYS} weekday={MATRIX_WEEKDAY} weekend={MATRIX_WEEKEND} marks={MATRIX_MARKS} windows={WINDOW_ROWS} />
      </Section>
      <Section title="맞춤" hint="칸의 너비는 구간 길이입니다. 줄을 고르면 오른쪽 정점이 바뀝니다.">
        <FitBoard headline="2개 단계가 좁고, 4개는 여유가 있습니다" sub="구축은 13주 중 1번 한계에 닿았습니다" rows={FIT_ROWS} />
      </Section>
    </div>
  );
}

const READINESS: ReadinessColumn[] = [
  { key: "analysis", name: "분석", elapsed: "착수 전", value: 8, verdict: "진행" },
  { key: "design", name: "설계", elapsed: "8일", value: 22, verdict: "진행" },
  { key: "master", name: "기준정보", elapsed: "18일", value: 31, verdict: "진행" },
  { key: "screen", name: "화면", elapsed: "26일", value: 34, verdict: "진행" },
  { key: "build", name: "구축", elapsed: "40일", value: 48, tone: "watch", callout: "기준보다 12%p 낮음", verdict: "주의" },
  { key: "test", name: "테스트", elapsed: "77일", value: 81, verdict: "진행" },
  { key: "cutover", name: "이행", elapsed: "94일", value: 95, tone: "good", verdict: "정상" },
  { key: "check", name: "검수", elapsed: "101일", value: 100, tone: "good", verdict: "정상" },
];

function ReadinessDemo() {
  const [selected, setSelected] = useState("build");
  return (
    <>
      <Section title="경과와 준비" hint="진행 중 플랜을 착수 후 지난 날 순으로 세웁니다. 빗금은 같은 무리이고, 기준에서 벗어난 막대만 단색과 말입니다.">
        <ReadinessChart
          count={6}
          countLabel="진행 중"
          standardLabel="올해 2개 기준 안"
          averageLabel="기준까지 평균 101일"
          columns={READINESS}
          selected={selected}
          onSelect={setSelected}
          note="막대를 고르면 아래 목록의 그 줄이 따라옵니다."
        />
      </Section>
      <Section title="목록 막대" hint="같은 준비를 줄에서 읽습니다. 길이가 비율이고, 숫자는 옆에 맞춥니다. 기준에 닿은 줄은 초록이고, 주의와 위험은 그 색입니다.">
        <ReadinessList title="진행 중 6" rows={READINESS} selected={selected} onSelect={setSelected} />
      </Section>
    </>
  );
}

function WeeklyInkDemo() {
  const styles = useStyles();
  const archive = WEEKLY_REPORT;
  const [weekId, setWeekId] = useState(() => latestWeek(archive).id);
  const week = weekById(archive, weekId) ?? latestWeek(archive);
  const through = weeksThrough(archive, week.id);
  const rows: InkVarianceRow[] = week.milestones.map((milestone) => {
    const owner = personById(archive, milestone.ownerId);
    const slip = slipText(milestoneSlipDays(milestone, week.asOf));
    const actual = milestone.actual ?? milestone.forecast;
    return {
      key: milestone.id,
      name: milestone.name,
      note: milestone.note,
      owner: owner.name,
      ownerRole: owner.role,
      plan: formatDate(milestone.planned, "numeric"),
      actual: actual ? formatDate(actual, "numeric") : "—",
      actualKind: milestone.actual ? "실적" : milestone.forecast ? "전망" : undefined,
      slip: slip.text,
      slipTone: slip.tone,
      status: RAG_LABEL[milestone.rag],
      statusTone: milestone.rag === "onTrack" ? "neutral" : ragTone(milestone.rag),
    };
  });
  return (
    <>
      <IssueBoards points={issueTrend(archive, through)} bands={issueAging(issuesForWeek(archive, week))} selectedId={week.id} onSelect={setWeekId} />
      <Section title="일정 차이" hint="양수는 계획보다 늦은 날입니다. 담당은 이름과 함께 둡니다.">
        <VarianceTable rows={rows} label="일정 차이 예시" />
      </Section>
      <Section title="상태 단어" hint="종합은 정상·주의·위험입니다. 차이는 달성·지연입니다. 표 안의 색은 주의와 위험에만 둡니다.">
        <div className={styles.words}>
          <StatusWord tone="good">정상</StatusWord>
          <StatusWord tone="watch">주의</StatusWord>
          <StatusWord tone="risk">위험</StatusWord>
          <StatusWord tone="risk">막힘</StatusWord>
          <StatusWord tone="watch">지연</StatusWord>
          <StatusWord tone="quiet">완료</StatusWord>
          <ProgressDelta gap={2} />
          <ProgressDelta gap={-7} />
        </div>
      </Section>
      <Section title="정규 주간보고 WBS 복합 그리드 (Report Grid)" hint="임원/고객사 보고용 흑백·회색조 WBS 단계별 계획/실적 및 파트별 산출물 매트릭스입니다.">
        <ReportWbsDemo />
      </Section>
    </>
  );
}

function disciplineText(info: { deliverable?: string; task?: string }): string {
  const deliverable = info.deliverable && info.deliverable !== "—" ? info.deliverable : "";
  const task = info.task && info.task !== "—" ? info.task : "";
  return [deliverable, task].filter(Boolean).join(" ") || "—";
}

function ReportWbsDemo() {
  const rows = WBS_SNAPSHOT_WEEK_10.slice(0, 10);
  return (
    <div style={{ overflowX: "auto", border: "1px solid #d1d5db", backgroundColor: "#ffffff" }}>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "12px", textAlign: "left" }}>
        <thead>
          <tr style={{ backgroundColor: "#f3f4f6", color: "#111827", fontWeight: 700, borderBottom: "1px solid #d1d5db" }}>
            <th style={{ padding: "6px 8px", border: "1px solid #d1d5db" }}>WBS</th>
            <th style={{ padding: "6px 8px", border: "1px solid #d1d5db" }}>액티비티명</th>
            <th style={{ padding: "6px 8px", border: "1px solid #d1d5db", textAlign: "center" }}>계획 (착수~완료)</th>
            <th style={{ padding: "6px 8px", border: "1px solid #d1d5db", textAlign: "center" }}>실적 (착수~완료)</th>
            <th style={{ padding: "6px 8px", border: "1px solid #d1d5db", textAlign: "right" }}>공정률</th>
            <th style={{ padding: "6px 8px", border: "1px solid #d1d5db" }}>PM / PMO</th>
            <th style={{ padding: "6px 8px", border: "1px solid #d1d5db" }}>설계</th>
            <th style={{ padding: "6px 8px", border: "1px solid #d1d5db" }}>구축</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr
              key={row.id}
              style={{
                backgroundColor: row.isPhase ? "#f8fafc" : "#ffffff",
                fontWeight: row.isPhase ? 700 : 400,
                borderBottom: "1px solid #e5e7eb",
              }}
            >
              <td style={{ padding: "6px 8px", border: "1px solid #e5e7eb", textAlign: "center", color: "#4b5563" }}>
                {row.code}
              </td>
              <td style={{ padding: "6px 8px", border: "1px solid #e5e7eb", paddingLeft: row.isPhase ? "8px" : "18px" }}>
                {row.name}
              </td>
              <td style={{ padding: "6px 8px", border: "1px solid #e5e7eb", textAlign: "center", fontVariantNumeric: "tabular-nums", fontSize: "11px", color: "#6b7280" }}>
                {row.planStart} ~ {row.planEnd}
              </td>
              <td style={{ padding: "6px 8px", border: "1px solid #e5e7eb", textAlign: "center", fontVariantNumeric: "tabular-nums", fontSize: "11px", color: "#374151" }}>
                {row.actualStart} ~ {row.actualEnd}
              </td>
              <td style={{ padding: "6px 8px", border: "1px solid #e5e7eb", textAlign: "right", fontVariantNumeric: "tabular-nums", fontWeight: 600 }}>
                {row.progress}%
              </td>
              <td style={{ padding: "6px 8px", border: "1px solid #e5e7eb", fontSize: "11px" }}>
                <span style={{ color: "#4b5563" }}>{disciplineText(row.pm)}</span>
              </td>
              <td style={{ padding: "6px 8px", border: "1px solid #e5e7eb", fontSize: "11px" }}>
                <span style={{ color: "#4b5563" }}>{disciplineText(row.design)}</span>
              </td>
              <td style={{ padding: "6px 8px", border: "1px solid #e5e7eb", fontSize: "11px" }}>
                <span style={{ color: "#4b5563" }}>{disciplineText(row.dev)}</span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const SHARE_PARTS = [
  { key: "plan", label: "계획" },
  { key: "actual", label: "실적" },
  { key: "extra", label: "추가" },
];

const SHARE_ROWS = [
  { key: "build", name: "구축", parts: { plan: 180, actual: null, extra: null } },
  { key: "test", name: "테스트", parts: { plan: 130, actual: null, extra: 20 } },
  { key: "design", name: "설계", parts: { plan: 62, actual: 18, extra: null } },
  { key: "cutover", name: "이행", parts: { plan: null, actual: 30, extra: 25 } },
  { key: "open", name: "오픈", parts: { plan: 10, actual: 25, extra: null } },
];

const UPCOMING = [
  { key: "test", date: "2026-11-01", title: "통합 테스트 착수 보고" },
  { key: "train", date: "2026-11-02", title: "사용자 교육" },
  { key: "open", date: "2026-11-09", title: "오픈 점검" },
  { key: "cut", date: "2026-11-09", title: "데이터 전환" },
].map((item) => ({
  key: item.key,
  day: formatDate(item.date, "day"),
  month: formatDate(item.date, "monthName"),
  title: item.title,
  when: daysAfter("2026-10-23", item.date),
}));

const RHYTHM_COLUMNS = ["2026-09-23", "2026-09-28", "2026-10-03", "2026-10-08", "2026-10-13", "2026-10-18", "2026-10-22"].map((date, index, all) =>
  index === all.length - 1 ? "오늘" : formatDate(date, "short"),
);

const RHYTHM_LANES: RhythmLane[] = [
  { key: "build", name: "구축", values: [20, 35, 48, 60, 70, 62, 58], delayed: [4] },
  { key: "test", name: "테스트", values: [0, 4, 8, 12, 28, 40, 36], delayed: [5] },
  { key: "design", name: "설계", values: [40, 70, 90, 100, 80, 20, 0] },
  { key: "cutover", name: "이행", values: [0, 0, 6, 10, 14, 18, 22] },
  { key: "analysis", name: "분석", values: [80, 60, 20, 0, 0, 0, 0] },
  { key: "open", name: "오픈", values: [0, 0, 0, 4, 8, 10, 12], delayed: [6] },
];

const HEAT_LEVELS = [
  [0, 1, 2, 1],
  [1, 2, 3, 2],
  [1, 3, 4, 3],
  [1, 2, 4, 2],
  [0, 2, 3, 2],
  [0, 1, 2, 1],
  [0, 0, 1, 1],
];

const HEAT_MARKS = new Set(["2:2", "3:1", "4:2", "5:1"]);

const WEEKDAYS = weekdayNames("short");
const HEAT_ROWS: RhythmHeatRow[] = [...WEEKDAYS.slice(1), WEEKDAYS[0] ?? ""].map((label, row) => ({
    key: label,
    label,
    cells: (HEAT_LEVELS[row] ?? []).map((level, column) => ({
      level,
      marked: HEAT_MARKS.has(`${row}:${column}`),
      label: `${label} ${["00", "06", "12", "18"][column] ?? ""}시${HEAT_MARKS.has(`${row}:${column}`) ? ", 지연" : ""}`,
    })),
  }));

const FORECAST_DAYS = ["지금", "토", "일", "월", "화", "수", "목"];

const FORECAST_ROWS: ForecastRow[] = [
  {
    key: "build",
    name: "구축",
    levels: [3, 4, 2, 1, 1, 0, 0],
    runsOut: 1,
    reset: 5,
    aside: "토 06:31에 막힘",
    late: true,
    usedPct: 56,
    evenPct: 20,
    paceText: "2.8배",
    paceFast: true,
    resetIn: "6일 후",
    detail: {
      used: [55, 100, 100, 100, 100, 100, 100],
      current: [55, 100, 100, 100, 100, 100, 100],
      even: [12, 26, 40, 54, 68, 82, 96],
      nowIndex: 0,
      runsOutIndex: 1,
      resetLabel: "수요일 18:08 리셋",
      caption: "구간의 20% 동안 56%를 썼습니다",
      pace: "2.8배",
      runsOut: "토 06:31",
      locked: "4일 12시간",
      paceNote: "최근 속도 · 하루에 쓴 비율",
      allowanceNote: "허용은 하루 7.8",
      allowance: 8,
      days: [
        { label: "토", value: 5 },
        { label: "일", value: 4 },
        { label: "월", value: 8 },
        { label: "화", value: 8 },
        { label: "수", value: 18, over: true },
        { label: "목", value: 44, over: true },
        { label: "오늘", value: 0 },
      ],
    },
  },
  {
    key: "test",
    name: "테스트",
    levels: [2, 2, 3, 3, 3, 2, 1],
    reset: 3,
    aside: "리셋 때 약 91%",
    usedPct: 30,
    evenPct: 33,
    paceText: "0.9배",
    resetIn: "5일 후",
    detail: {
      used: [30, 42, 55, 68, 74, 80, 86],
      current: [30, 50, 70, 90, 91, 91, 91],
      even: [14, 28, 42, 56, 70, 84, 98],
      nowIndex: 0,
      runsOutIndex: null,
      resetLabel: "화요일 21:26 리셋",
      caption: "이 속도면 리셋 때 약 91%입니다",
      pace: "고르게",
      runsOut: "리셋 전",
      locked: "없음",
      paceNote: "최근 속도 · 하루에 쓴 비율",
      allowanceNote: "허용은 하루 7.8",
      allowance: 8,
      days: [
        { label: "토", value: 4 },
        { label: "일", value: 5 },
        { label: "월", value: 6 },
        { label: "화", value: 7 },
        { label: "수", value: 6 },
        { label: "목", value: 5 },
        { label: "오늘", value: 4 },
      ],
    },
  },
  calmRow({
    key: "design",
    name: "설계",
    levels: [3, 3, 2, 1, 0, -1, -1],
    reset: 1,
    aside: "리셋 때 약 81%",
    usedPct: 74,
    evenPct: 92,
    paceText: "0.8배",
    resetIn: "15시간 후",
    caption: "이 속도면 리셋 때 약 81%입니다",
  }),
  calmRow({
    key: "cutover",
    name: "이행",
    levels: [1, 1, 2, 2, 2, 2, 1],
    reset: 6,
    aside: "리셋 때 약 73%",
    usedPct: 22,
    evenPct: 31,
    paceText: "0.7배",
    resetIn: "22일 후",
    caption: "이 속도면 리셋 때 약 73%입니다",
  }),
  calmRow({
    key: "analysis",
    name: "분석",
    levels: [2, 2, 2, 1, 1, 1, 0],
    aside: "리셋 때 약 71%",
    usedPct: 39,
    evenPct: 56,
    paceText: "0.7배",
    resetIn: "3일 후",
    caption: "이 속도면 리셋 때 약 71%입니다",
  }),
  calmRow({
    key: "train",
    name: "교육",
    levels: [2, 2, 3, 3, 2, 2, 1],
    aside: "리셋 때 약 62%",
    usedPct: 43,
    evenPct: 72,
    paceText: "0.6배",
    resetIn: "9일 후",
    caption: "이 속도면 리셋 때 약 62%입니다",
  }),
  calmRow({
    key: "data",
    name: "데이터",
    levels: [1, 1, 1, 2, 2, 1, 1],
    aside: "리셋 때 약 61%",
    usedPct: 21,
    evenPct: 35,
    paceText: "0.6배",
    resetIn: "21일 후",
    caption: "이 속도면 리셋 때 약 61%입니다",
  }),
  calmRow({
    key: "stable",
    name: "안정화",
    levels: [1, 1, 1, 1, 2, 1, 0],
    aside: "리셋 때 약 30%",
    usedPct: 14,
    evenPct: 47,
    paceText: "0.3배",
    resetIn: "4일 후",
    caption: "이 속도면 리셋 때 약 30%입니다",
  }),
  { key: "open", name: "오픈", levels: [1, 0, -1, -1, -1, -1, -1], aside: "전망할 만큼 지나지 않음", usedPct: 14, paceText: "알 수 없음", resetIn: "7일 후" },
  { key: "check", name: "검수", levels: [2, 2, 1, 1, 1, 0, 0], aside: "다시 연결하면 전망", usedPct: 40, paceText: "알 수 없음", resetIn: "2일 후" },
];

function calmRow(row: Omit<ForecastRow, "detail"> & { caption: string }): ForecastRow {
  const detail: ForecastDetail = {
    used: [18, 28, 36, 44, 52, 58, 64],
    current: [18, 30, 42, 54, 64, 72, 78],
    even: [14, 28, 42, 56, 70, 84, 98],
    nowIndex: 0,
    runsOutIndex: null,
    resetLabel: `${row.resetIn ?? "곧"} 리셋`,
    caption: row.caption,
    pace: row.paceText ?? "고르게",
    runsOut: "리셋 전",
    locked: "없음",
    paceNote: "최근 속도 · 하루에 쓴 비율",
    allowanceNote: "허용은 하루 7.8",
    allowance: 8,
    days: [
      { label: "토", value: 4 },
      { label: "일", value: 5 },
      { label: "월", value: 6 },
      { label: "화", value: 7 },
      { label: "수", value: 6 },
      { label: "목", value: 5 },
      { label: "오늘", value: 3 },
    ],
  };
  return { ...row, detail };
}

function daysAfter(from: string, to: string): string {
  return `${toDay(to) - toDay(from)}일 후`;
}

const MATRIX_HOURS = ["00", "03", "06", "09", "12", "15", "18", "21"];
const MATRIX_START = "2026-09-26";

const MATRIX_DAYS: MatrixDay[] = Array.from({ length: 30 }, (_, index) => {
  const date = addDays(MATRIX_START, index);
  const dow = weekdayOfDay(toDay(date));
  const weekend = dow === 0 || dow === 6;
  const hours = MATRIX_HOURS.map((hour) => {
    const hourNum = Number(hour);
    const busy = hourNum >= 9 && hourNum <= 15 ? 4 : hourNum === 6 || hourNum === 18 ? 2 : hourNum === 3 || hourNum === 21 ? 1 : 0;
    const dip = (index + hourNum) % 7 === 0 ? 1 : 0;
    let level = busy - (weekend ? 2 : 0) - dip;
    if (weekend && hourNum < 6) level = -1;
    level = Math.max(-1, Math.min(4, level));
    const ranOut = (index === 12 && hour === "09") || (index === 18 && hour === "12") || (index === 23 && hour === "15");
    return { level: ranOut ? 4 : level, ranOut, label: `${formatDate(date, "monthDayWeekday")} ${hour}시${ranOut ? ", 막힘" : ""}` };
  });
  return {
    key: date,
    week: dow === 1 || index === 0 ? formatDate(date, "short") : undefined,
    letter: formatDate(date, "weekday"),
    today: date === "2026-10-23",
    locked: index === 3 || index === 4 || index === 16 || index === 17,
    hours,
  };
});

function hourMean(weekend: boolean, hour: number): number {
  const values = MATRIX_DAYS.filter((day) => {
    const dow = weekdayOfDay(toDay(day.key));
    return (dow === 0 || dow === 6) === weekend;
  })
    .map((day) => day.hours[hour]?.level ?? -1)
    .filter((level) => level >= 0);
  if (values.length === 0) return 0;
  return values.reduce((sum, value) => sum + value, 0) / values.length;
}

const MATRIX_WEEKDAY = MATRIX_HOURS.map((_, hour) => hourMean(false, hour));
const MATRIX_WEEKEND = MATRIX_HOURS.map((_, hour) => hourMean(true, hour));
const MATRIX_MARKS = MATRIX_HOURS.map((_, hour) => MATRIX_DAYS.some((day) => day.hours[hour]?.ranOut));

const WINDOW_ROWS: WindowRow[] = [
  { key: "build", name: "구축", segments: [{ key: "a", level: 2, flex: 4 }, { key: "b", level: 4, flex: 3, hit: true }, { key: "c", level: 3, flex: 2 }, { key: "d", level: 2, flex: 2, current: true }] },
  { key: "test", name: "테스트", segments: [{ key: "a", level: 3, flex: 2 }, { key: "b", level: 1, flex: 2 }, { key: "c", level: 4, flex: 3, hit: true }, { key: "d", level: 2, flex: 2 }, { key: "e", level: 1, flex: 1, current: true }] },
  { key: "design", name: "설계", segments: [{ key: "a", level: 4, flex: 5, hit: true }, { key: "b", level: 1, flex: 3 }, { key: "c", level: 0, flex: 2, current: true }] },
  { key: "cutover", name: "이행", segments: [{ key: "a", level: 1, flex: 2 }, { key: "b", level: 2, flex: 3 }, { key: "c", level: 2, flex: 2 }, { key: "d", level: 1, flex: 2, current: true }] },
  { key: "analysis", name: "분석", segments: [{ key: "a", level: 2, flex: 4 }, { key: "b", level: 1, flex: 3 }, { key: "c", level: 0, flex: 3, current: true }] },
  { key: "train", name: "교육", segments: [{ key: "a", level: 0, flex: 2 }, { key: "b", level: 1, flex: 4 }, { key: "c", level: 3, flex: 3, hit: true }, { key: "d", level: 1, flex: 1, current: true }] },
  { key: "data", name: "데이터", segments: [{ key: "a", level: 1, flex: 6 }, { key: "b", level: 2, flex: 2 }, { key: "c", level: 0, flex: 2, current: true }] },
  { key: "open", name: "오픈", segments: [{ key: "a", level: 0, flex: 3 }, { key: "b", level: 1, flex: 4 }, { key: "c", level: 1, flex: 2, current: true }] },
];

function fitPeaks(values: number[], hits: number[]): FitPeak[] {
  return values.map((value, index) => ({
    key: String(index),
    label: index === 0 ? formatDate("2026-07-21", "short") : index === values.length - 1 ? "지금" : "",
    value,
    hit: hits.includes(index),
    now: index === values.length - 1,
  }));
}

const FIT_ROWS: FitRow[] = [
  {
    key: "build",
    name: "구축",
    verdict: "tight",
    windows: [
      { key: "a", flex: 3, level: 3, label: "8월" },
      { key: "b", flex: 4, level: 4, hit: true, label: "9월, 한계" },
      { key: "c", flex: 2, level: 2, current: true, label: "이번 구간" },
    ],
    detail: { average: "92%", atLimit: "1 / 13주", locked: "2일", unused: "없음", note: "정점 평균 92%입니다. 13주 중 한계는 1번입니다. 구간이 좁습니다.", averageValue: 92, peaks: fitPeaks([70, 78, 84, 90, 96, 100, 88, 94, 100, 86, 92, 90, 40], [5, 8]) },
  },
  {
    key: "test",
    name: "테스트",
    verdict: "tight",
    windows: [
      { key: "a", flex: 2, level: 2, label: "9월" },
      { key: "b", flex: 3, level: 4, hit: true, label: "10월, 한계" },
      { key: "c", flex: 1, level: 1, current: true, label: "이번 구간" },
    ],
    detail: { average: "81%", atLimit: "2 / 13주", locked: "없음", unused: "없음", note: "정점 평균 81%입니다. 13주 중 한계는 2번입니다. 구간이 좁습니다.", averageValue: 81, peaks: fitPeaks([40, 48, 55, 62, 70, 88, 100, 76, 84, 100, 72, 68, 30], [6, 9]) },
  },
  {
    key: "design",
    name: "설계",
    verdict: "fit",
    windows: [
      { key: "a", flex: 1, level: 2, label: "8월" },
      { key: "b", flex: 1, level: 3, label: "8월" },
      { key: "c", flex: 1, level: 2, label: "9월" },
      { key: "d", flex: 1, level: 4, hit: true, label: "10월, 한계" },
      { key: "e", flex: 1, level: 1, current: true, label: "이번 구간" },
    ],
    detail: { average: "56%", atLimit: "1 / 13주", locked: "없음", unused: "약 0", note: "정점 평균 56%입니다. 13주 중 한계는 1번입니다. 크기가 맞습니다.", averageValue: 56, peaks: fitPeaks([48, 62, 44, 70, 52, 58, 40, 66, 54, 100, 50, 46, 28], [9]) },
  },
  {
    key: "cutover",
    name: "이행",
    verdict: "fit",
    windows: [
      { key: "a", flex: 2, level: 1, label: "9월" },
      { key: "b", flex: 2, level: 2, label: "10월" },
      { key: "c", flex: 2, level: 2, current: true, label: "이번 구간" },
    ],
    detail: { average: "48%", atLimit: "0 / 13주", locked: "없음", unused: "약 0", note: "정점 평균 48%입니다. 13주 중 한계는 0번입니다. 크기가 맞습니다.", averageValue: 48, peaks: fitPeaks([20, 28, 36, 44, 52, 40, 48, 56, 42, 38, 50, 46, 22], []) },
  },
  {
    key: "analysis",
    name: "분석",
    verdict: "spare",
    windows: [
      { key: "a", flex: 1, level: 1, label: "구간" },
      { key: "b", flex: 1, level: 1, label: "구간" },
      { key: "c", flex: 1, level: 0, label: "구간" },
      { key: "d", flex: 1, level: 1, current: true, label: "이번 구간" },
    ],
    detail: { average: "22%", atLimit: "0 / 13주", locked: "없음", unused: "많음", note: "정점 평균 22%입니다. 13주 중 한계는 0번입니다. 여유가 있습니다.", averageValue: 22, peaks: fitPeaks([12, 18, 24, 16, 20, 28, 14, 22, 18, 26, 20, 16, 10], []) },
  },
  {
    key: "train",
    name: "교육",
    verdict: "spare",
    windows: [
      { key: "a", flex: 3, level: 0, label: "구간" },
      { key: "b", flex: 2, level: 1, current: true, label: "이번 구간" },
    ],
    detail: { average: "18%", atLimit: "0 / 13주", locked: "없음", unused: "많음", note: "정점 평균 18%입니다. 13주 중 한계는 0번입니다. 여유가 있습니다.", averageValue: 18, peaks: fitPeaks([8, 12, 16, 10, 20, 14, 18, 22, 12, 16, 14, 10, 8], []) },
  },
  {
    key: "data",
    name: "데이터",
    verdict: "spare",
    windows: [
      { key: "a", flex: 4, level: 1, label: "구간" },
      { key: "b", flex: 1, level: 0, current: true, label: "이번 구간" },
    ],
    detail: { average: "15%", atLimit: "0 / 13주", locked: "없음", unused: "많음", note: "정점 평균 15%입니다. 13주 중 한계는 0번입니다. 여유가 있습니다.", averageValue: 15, peaks: fitPeaks([10, 14, 12, 18, 8, 16, 20, 12, 14, 10, 16, 12, 6], []) },
  },
  {
    key: "open",
    name: "오픈",
    verdict: "spare",
    windows: [
      { key: "a", flex: 2, level: 0, label: "구간" },
      { key: "b", flex: 1, level: 1, current: true, label: "이번 구간" },
    ],
    detail: { average: "12%", atLimit: "0 / 13주", locked: "없음", unused: "많음", note: "정점 평균 12%입니다. 13주 중 한계는 0번입니다. 여유가 있습니다.", averageValue: 12, peaks: fitPeaks([6, 10, 8, 14, 12, 8, 16, 10, 12, 8, 14, 10, 4], []) },
  },
];
